import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";

import EmptyData from "@/components/EmptyData";
import Filters from "@/components/Filters";
import Button from "@mui/material/Button";
import {Pagination} from "@mui/material";
import AthleticTestCard from "@/components/CardsList/AthleticTestCard";
import ExerciseCard from "@/components/CardsList/ExerciseCard";
import GeneralCard from "@/components/CardsList/GeneralCard";
import GPSCard from "@/components/CardsList/GPSCard";
import ManagerFootballTeamCard from "@/components/CardsList/ManagerFootballTeamCard";
import MatchCard from "@/components/CardsList/MatchCard";
import MatchReportCard from "@/components/CardsList/MatchReportCard";
import MedicalCard from "@/components/CardsList/MedicalCard";
import MedicalRecordCard from "@/components/CardsList/MedicalRecordCard";
import PlayerMovementCard from "@/components/CardsList/PlayerMovementCard";
import RosterPlayerCard from "@/components/CardsList/RosterPlayerCard";
import RPECard from "@/components/CardsList/RPECard";
import RPEStatCard from "@/components/CardsList/RPEStatCard";
import ScoutClubCard from "@/components/CardsList/ScoutClubCard";
import ScoutingStaffCard from "@/components/CardsList/ScoutingStaffCard";
import ScoutingPlayerCard from "@/components/CardsList/ScoutingPlayerCard";
import ScoutReportCard from "@/components/CardsList/ScoutReportCard";
import StaffCard from "@/components/CardsList/StaffCard";
import TargetCard from "@/components/CardsList/TargetCard";
import TQRCard from "@/components/CardsList/TQRCard";
import TQRStatCard from "@/components/CardsList/TQRStatCard";
import TrainingCard from "@/components/CardsList/TrainingCard";
import VASCard from "@/components/CardsList/VASCard";
import VideoCard from "@/components/CardsList/VideoCard";
import ValuationCard from "@/components/CardsList/ValuationCard";

import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

import {getNestedValue, orderByDate} from "@/utils/functions";

import "./index.scss";

const DEFAULT_ITEMS_PER_PAGE = 20;

export default function CardsList(props) {
	const {
		breadcrumbs = false,
		customFilters = false,
		grouped = false,
		noEmptyGroups = false,
		tabbed = false,
		middleHeight = false,
		fitHeight = false,
		listConfiguration,
		title,
		totalItems,
		itemsPerPage,
		handlePageChanged,
		handleDelete,
		handleUpdate,
		sorted = true,
		groupSorted = true,
		defaultSortedField,
		defaultSortedOrder = "ascend",
		defaultGroupSortedOrder,
		sortOptions = [],
		handleSortChange,
		filtersData,
		forceOpened = false,
		type,
	} = props;

	const {t} = useTranslation();

	const [opened, setOpened] = useState(false);
	const [currentSortOption, setCurrentSortOption] = useState("");
	const [currentSortOrder, setCurrentSortOrder] = useState("");
	const [currentPage, setCurrentPage] = useState(1);

	useEffect(() => {
		if (sortOptions?.length) {
			const defaultColumn = sortOptions?.filter((c) => c.sortedByDefault)?.[0];
			if (defaultColumn) {
				setCurrentSortOption(defaultColumn.key);
				setCurrentSortOrder(defaultColumn.sortedOrderByDefault);
			}
		}
	}, []);

	useEffect(() => {
		if (forceOpened) {
			setOpened(true);
		}
	}, [forceOpened]);

	const renderElement = (element, key) => {
		let cardElement = (
			<>
				{listConfiguration.date && (
					<div className="entity-date">{listConfiguration.date(element)}</div>
				)}
				{listConfiguration.mainTitle && (
					<div className="entity-title">
						{listConfiguration.mainTitle(element)}
					</div>
				)}
				{listConfiguration.status && (
					<div
						className={"entity-status " + listConfiguration.status(element)}
					></div>
				)}
				{listConfiguration.icon && (
					<div className="entity-icon">{listConfiguration.icon(element)}</div>
				)}
				<div className="entity-data-container">
					<div className={"main-info-container" + (type ? " " + type : "")}>
						{listConfiguration.imageUrl ? (
							<div className="image">
								<img src={listConfiguration.imageUrl(element)} alt="" />
							</div>
						) : null}
						{(listConfiguration.title || listConfiguration.subtitle) && (
							<div className="info">
								{listConfiguration.title && (
									<span className="title">
										{listConfiguration.title(element)}
									</span>
								)}
								{listConfiguration.subtitle && (
									<span className="subtitle">
										{listConfiguration.subtitle(element)}
									</span>
								)}
							</div>
						)}
					</div>
					{listConfiguration.logo && listConfiguration.logo(element) && (
						<div className="image logo">
							<img src={listConfiguration.logo(element)} alt="" />
						</div>
					)}
					{(listConfiguration.text?.(element) ||
						listConfiguration.subtext?.(element) ||
						listConfiguration.actions) && <div className="separator" />}
					{(listConfiguration.text?.(element) ||
						listConfiguration.subtext?.(element)) && (
						<div className="info">
							{listConfiguration.text && listConfiguration.text(element) && (
								<span className="text">{listConfiguration.text(element)}</span>
							)}
							{listConfiguration.subtext &&
								listConfiguration.subtext(element) && (
									<span className="subtitle">
										{listConfiguration.subtext(element)}
									</span>
								)}
						</div>
					)}
					{listConfiguration.actions ? (
						<div className="actions justify-content-center my-1">
							{listConfiguration.actions.map((action, key) => {
								return (
									<div key={key} className="action">
										<Button
											variant={action.variant || "contained"}
											onClick={() => action.onClick(element)}
											color={action.color || "blue"}
											size="small"
										>
											<div className="action-content d-flex align-items-center">
												<div>{action.icon && action.icon}</div>
											</div>
										</Button>
									</div>
								);
							})}
						</div>
					) : null}
				</div>
			</>
		);

		const actionsProps = {};
		if (handleDelete) {
			actionsProps.handleDelete = handleDelete;
		}
		if (handleUpdate) {
			actionsProps.handleUpdate = handleUpdate;
		}

		switch (type) {
			case "exercise":
				cardElement = <ExerciseCard element={element} />;
				break;
			case "general":
				cardElement = <GeneralCard element={element} />;
				break;
			case "gps":
				cardElement = <GPSCard element={element} />;
				break;
			case "manager_football_team":
				cardElement = <ManagerFootballTeamCard element={element} />;
				break;
			case "match":
			case "match_calendar":
			case "match_player":
			case "scouting_match":
				cardElement = (
					<MatchCard element={element} type={type} matchKey={key} />
				);
				break;
			case "medical":
				cardElement = (
					<MedicalCard element={element} actions={listConfiguration?.actions} />
				);
				break;
			case "medical_record":
				cardElement = <MedicalRecordCard element={element} />;
				break;
			case "player_movement":
				cardElement = (
					<PlayerMovementCard
						element={element}
						actions={listConfiguration?.actions}
					/>
				);
				break;
			case "roster_player":
			case "roster_staff":
				cardElement = (
					<RosterPlayerCard
						element={element}
						handleDelete={
							listConfiguration?.handleDelete
								? () => listConfiguration?.handleDelete?.(element)
								: undefined
						}
					/>
				);
				break;
			case "rpe":
				cardElement = <RPECard element={element} />;
				break;
			case "rpe_stat":
				cardElement = <RPEStatCard element={element} />;
				break;
			case "scout_club":
				cardElement = <ScoutClubCard element={element} />;
				break;
			case "scouting_report":
				cardElement = <ScoutReportCard element={element} />;
				break;
			case "scouting_staff":
				cardElement = <ScoutingStaffCard element={element} />;
				break;
			case "scouting_database":
				cardElement = (
					<ScoutingPlayerCard element={element} {...actionsProps} />
				);
				break;
			case "staff":
				cardElement = <StaffCard element={element} />;
				break;
			case "target":
				cardElement = <TargetCard element={element} />;
				break;
			case "test":
				cardElement = <AthleticTestCard element={element} />;
				break;
			case "test_base":
				cardElement = <AthleticTestCard element={element} base />;
				break;
			case "tqr":
				cardElement = (
					<TQRCard
						element={element}
						handleEdit={listConfiguration.handleEdit}
						handleDelete={listConfiguration.handleDelete}
					/>
				);
				break;
			case "tqr_stat":
				cardElement = <TQRStatCard element={element} />;
				break;
			case "training":
				cardElement = <TrainingCard element={element} />;
				break;
			case "video":
				cardElement = <VideoCard element={element} />;
				break;
			case "valuation":
				cardElement = <ValuationCard element={element} />;
				break;
			case "match_report":
				cardElement = <MatchReportCard element={element} />;
				break;
			case "vas":
				cardElement = (
					<VASCard
						element={element}
						handleEdit={listConfiguration.handleEdit}
						handleDelete={listConfiguration.handleDelete}
					/>
				);
				break;
			default:
				break;
		}

		return cardElement;
	};

	const renderList = (data) => {
		return data?.map((element, key) => {
			let defaultGridClassName = "col-12 col-sm-6 col-xl-3";

			if (type === "scout_club") {
				defaultGridClassName = "col-12 col-sm-6";
			}
			if (type === "staff") {
				defaultGridClassName = "col-12 col-sm-6 col-lg-4 col-xl-3";
			}
			if (type === "test_base") {
				defaultGridClassName = "col-6 col-sm-4 col-xl-3";
			}
			if (type === "tqr_stat") {
				defaultGridClassName = "col-12 col-sm-6 col-lg-3";
			}
			if (type === "users") {
				defaultGridClassName = "col-6 col-sm-4 col-xl-3";
			}
			if (type === "general") {
				defaultGridClassName = "col-6 col-sm-4";
			}
			return (
				<div key={key} className={"entities-columns " + defaultGridClassName}>
					<div
						className={
							"single-entity-container" +
							(!listConfiguration.onClick ? " disabled" : "") +
							(listConfiguration.customClassName?.(element)
								? " " + listConfiguration.customClassName(element)
								: "") +
							(type ? " " + type : "") +
							(listConfiguration.selected?.(element) ? " selected" : "") +
							(element.today ? " today" : "")
						}
						onClick={() => {
							listConfiguration.onClick && listConfiguration.onClick(element);
						}}
					>
						{renderElement(element, key)}
					</div>
				</div>
			);
		});
	};

	const handlePageChange = (_, newPage) => {
		setCurrentPage(newPage);
		handlePageChanged(newPage);
	};

	const checkGroupedDataLength = (data) => {
		let arePlayers = false;

		Object.keys(data)?.forEach((k) => {
			if (data[k]?.length) {
				arePlayers = true;
			}
		});

		return arePlayers;
	};

	const sortData = (data) => {
		const fieldToSort = !sortOptions?.length
			? defaultSortedField
			: currentSortOption;
		const orderToSort = !sortOptions?.length
			? defaultSortedOrder
			: currentSortOrder;

		const fieldType =
			sortOptions?.filter((c) => c.key === fieldToSort)?.[0]?.type || "string";

		let sortedData = [];
		switch (fieldType) {
			case "date":
				sortedData = orderByDate(data, fieldToSort, orderToSort);
				break;
			case "string":
			default:
				sortedData = data?.sort((a, b) => {
					const valueA = getNestedValue(a, fieldToSort);
					const valueB = getNestedValue(b, fieldToSort);

					return orderToSort === "descend"
						? valueB > valueA
							? 1
							: valueA > valueB
								? -1
								: 0
						: valueA > valueB
							? 1
							: valueB > valueA
								? -1
								: 0;
				});
				break;
		}
		return sortedData;
	};

	const sortedData =
		!grouped && defaultSortedField && sorted
			? sortData(listConfiguration?.data)
			: listConfiguration?.data;

	const groupedSortedOrder = defaultGroupSortedOrder || defaultSortedOrder;

	const sortedKeys = sortedData ? Object.keys(sortedData) : [];
	if (groupSorted) {
		sortedKeys.sort((a, b) =>
			groupedSortedOrder === "descend"
				? b.localeCompare(a)
				: a.localeCompare(b),
		);
	}

	useEffect(() => {
		if (handleSortChange && currentSortOption && currentSortOrder) {
			handleSortChange(currentSortOption, currentSortOrder);
		}
	}, [currentSortOption, currentSortOrder]);

	return (
		<>
			{filtersData && (
				<Filters
					data={filtersData}
					opened={opened}
					setOpened={setOpened}
					forceOpened={forceOpened}
				/>
			)}
			{
				<>
					{sortOptions?.length ? (
						<div className="sort-options-container">
							<div>{t("sort_by")}</div>
							<div className="options-container">
								{sortOptions.map((o, k) => {
									return (
										<div
											className={
												"option-container" +
												(currentSortOption === o.key ? " selected" : "")
											}
											key={k}
											onClick={() => {
												setCurrentSortOption(o.key);

												if (!currentSortOrder) {
													setCurrentSortOrder("ascend");
												}
											}}
										>
											{o.label}
										</div>
									);
								})}
							</div>
							<div className="vertical-line" />
							<div className="options-container">
								<div
									className={
										"option-container small" +
										(currentSortOrder === "ascend" ? " selected" : "")
									}
									onClick={() => setCurrentSortOrder("ascend")}
								>
									<ArrowDownwardIcon fontSize="small" />
								</div>
								<div
									className={
										"option-container small" +
										(currentSortOrder === "descend" ? " selected" : "")
									}
									onClick={() => setCurrentSortOrder("descend")}
								>
									<ArrowUpwardIcon fontSize="small" />
								</div>
							</div>
						</div>
					) : null}
					<div
						className={
							"groups-container" +
							(fitHeight ? " fit-height" : "") +
							(breadcrumbs ? " breadcrumbs" : "") +
							(handlePageChanged ? " paginated" : "") +
							(filtersData ? (opened ? " fixed-filters" : " filters") : "") +
							(customFilters ? " custom-filters" : "") +
							(sortOptions?.length ? " sorted" : "") +
							(tabbed ? " tabbed" : "") +
							(middleHeight ? " middle-height" : "")
						}
					>
						{grouped ? (
							checkGroupedDataLength(sortedData) ? (
								sortedKeys
									.filter((element) =>
										filtersData?.filter(
											(f) => f.key === "categoryId" && f.value,
										)?.length || noEmptyGroups
											? sortedData[element]?.length
											: true,
									)
									.map((element, key) => {
										return (
											<div
												key={key}
												className={
													"row no-margin entities-container g-1 grouped compact"
												}
											>
												<div className="group-title padding-small">
													{element}
												</div>
												{sortedData[element]?.length ? (
													renderList(sortData(sortedData[element]))
												) : (
													<EmptyData />
												)}
											</div>
										);
									})
							) : (
								<EmptyData />
							)
						) : (
							<div className={"row no-margin entities-container g-1 compact"}>
								<div className="title padding-small">{title}</div>
								{sortedData?.length ? renderList(sortedData) : <EmptyData />}
							</div>
						)}
					</div>
					{totalItems > (itemsPerPage || DEFAULT_ITEMS_PER_PAGE) && (
						<div className="pagination-container">
							<Pagination
								count={Math.ceil(
									totalItems / (itemsPerPage || DEFAULT_ITEMS_PER_PAGE),
								)}
								page={currentPage}
								onChange={handlePageChange}
								color="primary"
							/>
						</div>
					)}
				</>
			}
		</>
	);
}
